import React, { useState, useEffect } from "react";
import "./Orders.scss";
import {
  Autocomplete,
  InputAdornment,
  TextField,
  useMediaQuery,
  Pagination,
  Stack,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import GreenButton from "../../components/GreenButton/GreenButton";
import Loader from "../../components/Loader/Loader";
import OrderCard from "../../components/OrderCard/OrderCard";
import api from "../../utils/api";
import { useLanguage } from "../../context/LanguageContext";
import { useWebSocket } from "../../context/WebSocketContext"; // Add this import

const Orders = () => {
  const { t } = useLanguage();
  const { orderUpdateTrigger } = useWebSocket(); // Add this to get the trigger
  const isMobile = useMediaQuery("(max-width:767px)");
  const [type, setType] = useState("current");
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortOrder, setSortOrder] = useState("nearest");

  // Pagination state
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit] = useState(10);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, [page]);

  const fetchOrders = async () => {
    setIsLoading(true);
    try {
      const response = await api.get(
        `/bookings/me?type=${type}&page=${page}&limit=${limit}`
      );

      const { data, totalPages } = response.data;
      setOrders(data);
      setTotalPages(totalPages);
    } catch (error) {
      console.error("Error fetching orders:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [type, page, limit]);

  // Add this useEffect to listen for WebSocket order updates
  useEffect(() => {
    if (orderUpdateTrigger > 0) {
      console.log("Order update detected, refreshing orders...");
      fetchOrders();
    }
  }, [orderUpdateTrigger]);

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  // Filter orders based on search query
  const filteredOrders = orders.filter((order) =>
    order.orderNumber?.toString().includes(searchQuery)
  );

  // Sort orders by date
  const sortedOrders = [...filteredOrders].sort((a, b) => {
    // First compare dates
    const dateA = new Date(a.date);
    const dateB = new Date(b.date);

    // If dates are the same, compare by time
    if (dateA.getTime() === dateB.getTime()) {
      const timeA = a.time.from;
      const timeB = b.time.from;
      return sortOrder === "nearest"
        ? timeA.localeCompare(timeB)
        : timeB.localeCompare(timeA);
    }

    // Sort by date
    return sortOrder === "nearest"
      ? dateA.getTime() - dateB.getTime()
      : dateB.getTime() - dateA.getTime();
  });

  const handleTypeChange = (newType) => {
    setType(newType);
    setPage(1);
  };

  // Handle sort order change
  const handleSortChange = (event) => {
    setSortOrder(event.target.value);
  };

  return (
    <div className="orders">
      <div className="container">
        <h2 className="section-title">{t("orders")}</h2>
        <div className="header">
          <div className="buttons-container">
            <GreenButton
              text={t("currentOrders")}
              variant={type === "current" ? "fill" : "outlined"}
              size="medium"
              onClick={() => handleTypeChange("current")}
            />
            <GreenButton
              text={t("completedOrders")}
              variant={type === "completed" ? "fill" : "outlined"}
              size="medium"
              onClick={() => handleTypeChange("completed")}
            />
          </div>

          <div className="search-sort">
            <div className="search">
              <Autocomplete
                sx={{ width: isMobile ? "100%" : "300px" }}
                id="autocomplete-demo"
                disableClearable
                options={[]}
                onInputChange={(event, newValue) => {
                  setSearchQuery(newValue);
                }}
                renderInput={(params) => (
                  <TextField
                    sx={{
                      backgroundColor: "white",
                    }}
                    placeholder={t("orderNumberPlaceholder")}
                    {...params}
                    InputProps={{
                      ...params.InputProps,
                      type: "search",
                      endAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
            </div>

            {/* Add sort selector */}
            <FormControl sx={{ minWidth: 120, backgroundColor: "white" }}>
              <InputLabel id="sort-order-label">{t("sortBy")}</InputLabel>
              <Select
                labelId="sort-order-label"
                id="sort-order"
                value={sortOrder}
                label={t("sortBy")}
                onChange={handleSortChange}
                sx={{ padding: "0px", height: "44px" }}
              >
                <MenuItem value="nearest">{t("nearestFirst")}</MenuItem>
                <MenuItem value="farthest">{t("farthestFirst")}</MenuItem>
              </Select>
            </FormControl>
          </div>
        </div>

        {isLoading ? (
          <Loader text={t("loadingOrders")} />
        ) : sortedOrders.length > 0 ? (
          <>
            <div className="orders-container">
              <div className="wrapper">
                {sortedOrders.map((order) => (
                  <OrderCard
                    key={order._id}
                    order={order}
                    onCancelOrder={() => fetchOrders()}
                  />
                ))}
              </div>
            </div>

            {/* Pagination component */}
            {totalPages > 1 && (
              <Stack spacing={2} alignItems="center" sx={{ mt: 4, mb: 4 }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={handlePageChange}
                  color="primary"
                  shape="rounded"
                  dir="ltr" // Keep pagination LTR for both languages
                />
              </Stack>
            )}
          </>
        ) : (
          <div className="empty-state">
            <div className="empty-icon">📋</div>
            <h3 className="empty-title">
              {type === "current"
                ? t("noCurrentOrders")
                : t("noCompletedOrders")}
            </h3>
            <p className="empty-description">
              {type === "current"
                ? t("noCurrentOrdersDescription")
                : t("noCompletedOrdersDescription")}
            </p>
            {type === "current" && (
              <button
                className="browse-packages-btn"
                onClick={() => (window.location.href = "/services")}
              >
                {t("browseServices")}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Orders;
